import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { mediaSrc } from '../api/media';

interface ChatSummary {
  conversationId: number;
  type: 'DIRECT' | 'GROUP' | 'PARENT_KID';
  title: string;
  photoUrl: string | null;
  lastMessageAt: string | null;
  lastPreview: string | null;
  unread: number;
  closed: boolean;
}
interface Message {
  id: number;
  senderType: 'PARENT' | 'KID';
  senderId: number;
  senderName: string;
  body: string;
  createdAt: string;
  mine: boolean;
}
interface KidCandidate {
  kidId: number;
  name: string;
  photoUrl: string | null;
  parentName: string | null;
  mine: boolean;
}

const ParentChat: React.FC = () => {
  const [params] = useSearchParams();
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [showKidPicker, setShowKidPicker] = useState(false);
  const [candidates, setCandidates] = useState<KidCandidate[]>([]);
  const lastIdRef = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadChats = useCallback(async () => {
    try { setChats((await api.get('/api/parent/chats')).data); } catch {}
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);

  // auto-open a conversation passed via ?cid=
  useEffect(() => {
    const cid = params.get('cid');
    if (cid) setActiveId(Number(cid));
  }, [params]);

  // full reload when switching conversation
  useEffect(() => {
    if (activeId == null) return;
    let cancelled = false;
    lastIdRef.current = 0;
    setMessages([]);
    api.get(`/api/parent/chats/${activeId}/messages`).then((r) => {
      if (cancelled) return;
      setMessages(r.data);
      if (r.data.length) lastIdRef.current = r.data[r.data.length - 1].id;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeId]);

  // poll for new messages every 3s
  useEffect(() => {
    if (activeId == null) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/api/parent/chats/${activeId}/messages`, { params: { afterId: lastIdRef.current } });
        if (r.data.length) {
          setMessages((prev) => [...prev, ...r.data]);
          lastIdRef.current = r.data[r.data.length - 1].id;
        }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || activeId == null) return;
    const body = text;
    setText('');
    try {
      const r = await api.post(`/api/parent/chats/${activeId}/messages`, { body });
      setMessages((prev) => [...prev, r.data]);
      lastIdRef.current = r.data.id;
      loadChats();
    } catch { setText(body); }
  };

  const openKidPicker = async () => {
    setShowKidPicker(true);
    try { setCandidates((await api.get('/api/parent/chats/kid-candidates')).data); } catch { setCandidates([]); }
  };

  const startKidChat = async (kidId: number) => {
    try {
      const r = await api.post(`/api/parent/chats/kid/${kidId}`);
      setShowKidPicker(false);
      await loadChats();
      setActiveId(r.data.conversationId);
    } catch { alert('Could not start chat'); }
  };

  const endChat = async () => {
    if (activeId == null || !window.confirm('End this chat? Your kid will lose access until you reopen it.')) return;
    try { await api.post(`/api/parent/chats/${activeId}/close`); await loadChats(); } catch { alert('Could not end chat'); }
  };

  const reopenChat = async (cid: number) => {
    try {
      const r = await api.post(`/api/parent/chats/${cid}/reopen`);
      await loadChats();
      setActiveId(r.data.conversationId);
    } catch { alert('Could not reopen chat'); }
  };

  const active = chats.find((c) => c.conversationId === activeId);

  const icon = (c: ChatSummary) => {
    if (c.type === 'PARENT_KID') return <span className="w-9 h-9 rounded-full bg-amber-100 inline-flex items-center justify-center">🧒</span>;
    if (c.type === 'DIRECT' && c.photoUrl) return <img src={mediaSrc(c.photoUrl)} alt="" className="w-9 h-9 rounded-full object-cover" />;
    return <span className="w-9 h-9 rounded-full bg-indigo-100 inline-flex items-center justify-center">{c.type === 'GROUP' ? '👪' : '👤'}</span>;
  };

  return (
    <div className="flex h-[68vh] rounded-2xl overflow-hidden border border-gray-100 bg-white">
      {/* chat list */}
      <div className={`${activeId != null ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 border-r border-gray-200 bg-white`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h1 className="text-lg font-bold text-parentaccent">💬 Chat</h1>
          <button onClick={openKidPicker}
            className="text-xs text-parentaccent border border-indigo-200 hover:bg-indigo-50 px-2.5 py-1 rounded-lg">+ Kid chat</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {chats.length === 0 ? (
            <p className="text-sm text-gray-400 p-4">No chats yet. Start one from Friends, Groups, or “+ Kid chat”.</p>
          ) : chats.map((c) => (
            <button key={c.conversationId} onClick={() => c.closed ? reopenChat(c.conversationId) : setActiveId(c.conversationId)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 ${activeId === c.conversationId ? 'bg-indigo-50' : ''} ${c.closed ? 'opacity-60' : ''}`}>
              {icon(c)}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {c.title}
                  {c.type === 'PARENT_KID' && <span className="ml-1 text-[10px] text-amber-500">· kid</span>}
                  {c.closed && <span className="ml-1 text-[10px] text-gray-400">· ended</span>}
                </p>
                <p className="text-xs text-gray-400 truncate">{c.closed ? 'Tap to reopen' : (c.lastPreview || 'No messages yet')}</p>
              </div>
              {c.unread > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{c.unread}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* thread */}
      <div className={`${activeId == null ? 'hidden md:flex' : 'flex'} flex-col flex-1`}>
        {active == null ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a chat</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center gap-2">
              <button onClick={() => setActiveId(null)} className="md:hidden text-parentaccent">←</button>
              <span className="font-semibold text-gray-700">{active.title}</span>
              <span className="text-xs text-gray-400">{active.type === 'GROUP' ? '· group' : active.type === 'PARENT_KID' ? '· kid' : ''}</span>
              {active.type === 'PARENT_KID' && !active.closed && (
                <button onClick={endChat} className="ml-auto text-xs text-red-400 hover:text-red-600 border border-red-200 px-2.5 py-1 rounded-lg">End chat</button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.mine ? 'bg-parentaccent text-white' : 'bg-white text-gray-700 shadow-sm'}`}>
                    {!m.mine && active.type === 'GROUP' && <p className="text-[11px] font-semibold text-indigo-500 mb-0.5">{m.senderName}</p>}
                    {m.body}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {active.closed ? (
              <div className="p-3 border-t border-gray-200 bg-gray-50 text-center text-sm text-gray-500 flex items-center justify-center gap-3">
                Chat ended.
                <button onClick={() => reopenChat(active.conversationId)} className="text-parentaccent border border-indigo-200 hover:bg-indigo-50 px-3 py-1 rounded-lg">Reopen</button>
              </div>
            ) : (
              <form onSubmit={send} className="p-3 border-t border-gray-200 bg-white flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
                  className="flex-1 border rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                <button type="submit" className="bg-parentaccent text-white text-sm px-4 py-2 rounded-full">Send</button>
              </form>
            )}
          </>
        )}
      </div>

      {/* kid picker modal */}
      {showKidPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowKidPicker(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-700">Start a chat with a kid</h3>
              <button onClick={() => setShowKidPicker(false)} className="text-gray-400 text-xl leading-none">×</button>
            </div>
            {candidates.length === 0 ? (
              <p className="text-sm text-gray-400">No kids available. Your own kids and friends’ kids shared with you appear here.</p>
            ) : (
              <div className="space-y-2">
                {candidates.map((k) => (
                  <button key={k.kidId} onClick={() => startKidChat(k.kidId)}
                    className="w-full flex items-center gap-3 text-left px-2 py-2 rounded-lg hover:bg-gray-50">
                    {k.photoUrl ? (
                      <img src={mediaSrc(k.photoUrl)} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <span className="w-9 h-9 rounded-full bg-amber-100 inline-flex items-center justify-center">🧒</span>
                    )}
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-700 truncate">{k.name}</span>
                      <span className="block text-xs text-gray-400 truncate">{k.mine ? 'Your kid' : `Shared by ${k.parentName}`}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentChat;
